/*
 * Poundsale header behaviour (sections/ps-header.liquid)
 *  - off-canvas mobile menu (focus trap, Escape, overlay click, scroll lock)
 *  - publishes --header-height (+ body.header-pinned when sticky) so Hyper's sticky helpers keep working
 *  - Escape closes the live search results (Hyper's <predictive-search> element)
 */
if (!customElements.get('ps-header')) {
  customElements.define(
    'ps-header',
    class PsHeader extends HTMLElement {
      connectedCallback() {
        this.header = this.querySelector('.ps-header');
        this.drawer = this.querySelector('.ps-drawer');
        this.burger = this.querySelector('.ps-header__burger');
        this.sticky = this.dataset.sticky === 'true';

        this.measure = this.measure.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onResize = this.onResize.bind(this);

        this.setupDrawer();
        this.setupSearch();

        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.onResize, { passive: true });

        if (window.ResizeObserver && this.header) {
          this.resizeObserver = new ResizeObserver(this.measure);
          this.resizeObserver.observe(this.header);
        }
        this.measure();
        if (this.sticky) document.body.classList.add('header-pinned');
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.onResize);
        this.resizeObserver && this.resizeObserver.disconnect();
        this.closeDrawer(false);
        document.body.classList.remove('header-pinned');
      }

      /* ---------------------------------------------------------------- */
      measure() {
        if (!this.header) return;
        const height = Math.round(this.header.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }

      onResize() {
        if (window.innerWidth >= 990 && this.isDrawerOpen) this.closeDrawer(false);
      }

      /* ---------------------------------------------------------------- */
      /* Mobile drawer                                                     */
      /* ---------------------------------------------------------------- */
      get isDrawerOpen() {
        return !!this.drawer && this.drawer.classList.contains('is-open');
      }

      setupDrawer() {
        if (!this.drawer || !this.burger) return;
        this.drawer.setAttribute('inert', '');
        this.burger.addEventListener('click', () => (this.isDrawerOpen ? this.closeDrawer(true) : this.openDrawer()));
        this.drawer.querySelectorAll('[data-ps-close]').forEach((el) => {
          el.addEventListener('click', () => this.closeDrawer(true));
        });
      }

      openDrawer() {
        if (!this.drawer) return;
        this.drawer.removeAttribute('inert');
        this.drawer.classList.add('is-open');
        this.burger.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('ps-drawer-open');
        const first = this.drawer.querySelector('.ps-drawer__close');
        requestAnimationFrame(() => first && first.focus());
      }

      closeDrawer(restoreFocus = true) {
        if (!this.drawer) return;
        const wasOpen = this.isDrawerOpen;
        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('inert', '');
        this.burger && this.burger.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('ps-drawer-open');
        if (wasOpen && restoreFocus && this.burger) this.burger.focus();
      }

      trapFocus(event) {
        const focusable = Array.from(
          this.drawer.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
        ).filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ---------------------------------------------------------------- */
      /* Search                                                            */
      /* ---------------------------------------------------------------- */
      setupSearch() {
        this.search = this.querySelector('predictive-search');
      }

      closeSearch() {
        if (this.search && this.search.classList.contains('predictive-search-open')) {
          this.search.toggleSearchState(false);
          const input = this.search.querySelector('input[type="search"]');
          input && input.blur();
          return true;
        }
        return false;
      }

      /* ---------------------------------------------------------------- */
      onKeydown(event) {
        if (event.key === 'Escape') {
          if (this.isDrawerOpen) {
            this.closeDrawer(true);
            return;
          }
          if (this.closeSearch()) return;
        } else if (event.key === 'Tab' && this.isDrawerOpen) {
          this.trapFocus(event);
        }
      }
    }
  );
}
